/**
 * 业务页面中 配合宽表组件 PerformanceMetricsTable使用的逻辑
 */
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useIntl, getLocale } from "umi";
import moment from "moment";
import MetricAndUpDown from "@/pages/dashboard/components/MetricAndUpDown";
import { Bubble } from "tea-component";
import {
  formatAccumulation, formatFilterValues, formatPercentNum, formatNum, commonValueFormatter,
  generateCopyData, getUpAndDownStatus, getWeekDay, getDynamicLocalCnEnText
} from '@/pages/dashboard/utils/utils';
import { IConfig, ICustomSelectorDimensionConfig, PagingQuery, SortBy, TableParamsType, columsType, metricsInfoType } from "./types";
import { getExplainVal } from "@/pages/dashboard/utils/getExplainVal";
import { TABLE_CELL_PADDING_LEFT, TABLE_CELL_PADDING_RIGHT } from "@/pages/home/_config/config";
// import { thousandthFormat } from "@/pages/home/utils/common";
import SvgIcon from "@/pages/home/components/SvgIcon";
import { getCountryFlag } from "@/pages/home/_config/countries";
import { IExplain } from "../../types/dashboard";
import { ICustomMetrics } from "../../pages/overview/components/OverviewTableCo/types";
import { get } from "lodash-es";
import dataFormat from "@home/utils/dbDataFormat/dataFormat";
import dataCommonFormat from "@home/utils/dbDataFormat/dataCommonFormat";
import { dynamicString } from "@home/utils/dynamicString";
import { resolveAndSelectName } from "../../utils/metricUtils";

const defaultSelectorDimensions: never[] = [];
export function useTableState() {
  // 加载状态
  const [loading, setLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  // 分页
  const [paging, setPaging] = useState<PagingQuery>({ pageSize: 10, pageIndex: 1 });
  // 排序
  const [sort, setSort] = useState<SortBy[]>([]);
  // 总条数
  const [total, setTotal] = useState(0);

  // 表格数据
  const [tableData, setTableData] = useState([]);

  // 聚合数据
  const [statisticsData, setStatisticsData] = useState([]);
  return {
    loading, setLoading,
    isError, setIsError,
    paging, setPaging,
    sort, setSort,
    total, setTotal,
    tableData, setTableData,
    statisticsData, setStatisticsData,
  };
}

/**
 * 针对 customFiltersConfig.customSelectorsDimensions 的通用 Hook：
 * - 负责拆分 selector 配置（列 / 聚合 / UI）
 * - 维护 selector 当前选中值（selectorValues + ref）
 * - 提供构造 filters.custom_filters 的工具函数
 */
export function useCustomSelectors(
  customSelectorsDimensionsConfig?: ICustomSelectorDimensionConfig[],
) {
  const selectorDimensions = customSelectorsDimensionsConfig || defaultSelectorDimensions;

  // 宽表中需要追加的 selector 维度列 key 列表
  const selectorColumnKeys = useMemo(
    () => selectorDimensions?.filter(d => d.showColumn).map(d => d.key),
    [selectorDimensions],
  );

  // 需要参与 accumulation.custom_accumulation 的 selector 维度 key 列表
  const includeCustomAccumulationKeys = useMemo(
    () => selectorDimensions?.filter(d => d.includeInAccumulation).map(d => d.key),
    [selectorDimensions],
  );

  // 需要在页面顶部展示为 Selector 单选的维度配置
  const selectorDimensionsForUi = useMemo(
    () => selectorDimensions?.filter(d => d.showSelector),
    [selectorDimensions],
  );

  // selector 当前选中值（按 key 存储），初始值使用 defaultValue
  const [selectorValues, setSelectorValues] = useState<Record<string, string | undefined>>(() => {
    const init: Record<string, string | undefined> = {};
    selectorDimensions?.forEach(d => {
      init[d.key] = d.defaultValue;
    });
    return init;
  });

  // 提供最新 selectorValues 的 ref，便于在闭包中访问
  const selectorValuesRef = useRef<Record<string, string | undefined>>(selectorValues);
  useEffect(() => {
    selectorValuesRef.current = selectorValues;
  }, [selectorValues]);

  // 当配置变化时，给新增的维度 key 补上 defaultValue（保留已有用户选择）
  useEffect(() => {
    if (!selectorDimensions || selectorDimensions.length === 0) return;
    setSelectorValues(prev => {
      const next = { ...prev };
      selectorDimensions.forEach(d => {
        if (next[d.key] === null || next[d.key] === undefined) {
          next[d.key] = d.defaultValue;
        }
      });
      return next;
    });
  }, [selectorDimensions]);

  /** custom_filters_merge 项类型：key + filter_list */
  type CustomFilterMergeItem = { key: string; filter_list: string[]; };

  // 根据给定的值映射构造 filters.custom_filters
  const buildCustomFiltersMerge = useMemo(() => {
    return (valuesMap: Record<string, string | undefined>): CustomFilterMergeItem[] | undefined => {
      const list = selectorDimensions?.map(d => {
        const value = valuesMap?.[d.key] ?? d.defaultValue;
        if (!value) return null;
        return { key: d.key, filter_list: [value] };
      })
        .filter(Boolean);
      if (list?.length === 0) return undefined;
      return list;
    };
  }, [selectorDimensions]);

  // 实际需要展示的 selector 维度列 key 列表（选「全部」时隐藏该列）
  const selectorColumnKeysVisible = useMemo(() => {
    return (
      selectorColumnKeys?.filter(key => {
        const dim = selectorDimensions?.find(d => d.key === key);
        const selectedValue = selectorValues[key];
        const isAllOption = dim?.options?.find(o => o.value === selectedValue)?.isAll === true;
        return !isAllOption;
      }) ?? []
    );
  }, [selectorColumnKeys, selectorValues, selectorDimensions]);

  return {
    selectorDimensions,
    selectorColumnKeys,
    selectorColumnKeysVisible,
    includeCustomAccumulationKeys,
    selectorDimensionsForUi,
    selectorValues,
    setSelectorValues,
    selectorValuesRef,
    buildCustomFiltersMerge,
  };
}

// 表格上传参数
export function getTableRequestParams({
  gameCode,
  filterValue: formVal,
  accumulated,
  sort,
  paging,
  selectType,
  ...resetParams
}: TableParamsType) {
  const { by, order } = sort?.[0] || { by: 'date', order: 'desc' };
  const { rangeTime, timeType, singleDaily, diff, ...filters } = formVal || {};
  const [start, end] = rangeTime || [];

  const params = {
    game_code: gameCode,
    filters: formatFilterValues(filters),
    accumulation: formatAccumulation(accumulated),
    page: paging.pageIndex,
    page_size: paging.pageSize,
    sorted_by: by?.replace(selectType, ''),
    desc: order === 'desc',
    start_date: start,
    end_date: end,
    ...resetParams
  };
  return params;
}


export function useTableColumns({ isAccumulated, config, hasDate, timeType }: { isAccumulated: boolean; config: IConfig; hasDate: boolean; timeType: string; }) {
  const { formatMessage } = useIntl();
  const [isIconHover, setIsIconHover] = useState(false);

  const isEn = getLocale() === 'en-US';
  // 日期列, 周六日的颜色高亮,
  const DATE_COLUMN = {
    key: 'date',
    copyKey: 'date',
    header: formatMessage({ id: 'common_date' }),
    // 取消分日期 周粒度需要展示到日
    width: isAccumulated || (timeType === 'Week' && !hasDate) ? 220 : 160,
    fixed: 'left',
    isDimColumn: true,
    align: 'left',
    render(record: { date: string; }) {
      const reg = /\((.+?)\)/gi;
      const weekValue = record?.date?.match(reg)?.[0]?.replace(/^\(/, '')?.slice(0, -1);
      let weekStr = '';
      if (weekValue) {
        if (weekValue?.includes('Null') || weekValue?.includes('去空')) {
          weekStr = `(${weekValue})`;
        } else {
          weekStr = weekValue;
        }
      }
      const dateVal = record?.date ? record?.date?.split('(') : ['-'];
      const isSunOrSat = weekStr === 'Sun' || weekStr === 'Sat' || weekStr === '周日' || weekStr === '周六';
      const showSumTip = record?.date === 'Sum' || record?.date === '汇总值';
      return (
        <div className='dbd-overiew-table-date-columns-box'>
          <span className='table-text'>{dateVal?.[0]}</span>
          <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekStr}</span>
          {showSumTip && (
            <div className='port-item'>
              <div
                onMouseOver={() => {
                  setIsIconHover(true);
                }}
                onMouseOut={() => {
                  setIsIconHover(false);
                }}
              >
                <Bubble
                  dark
                  placement='top-start'
                  arrowPointAtCenter
                  style={{ maxWidth: isEn ? '235px' : '253px' }}
                  content={formatMessage({ id: 'overview_sum_tip' })}
                >
                  <div>
                    <SvgIcon className='port-icon' type={isIconHover ? 'alert-circle-action' : 'alert-circle-m'} />
                  </div>
                </Bubble>
              </div>
            </div>
          )}
        </div>
      );
    }
  };

  // Accumulate 列
  const getAccumulateColumns = (
    accumulates: string[] = [],
    explainInfo: IExplain,
    isShowCountryFlag = true,
    isRevenueCustomAccumulate = false,
    gameCode?: string,
    selector?: unknown[],
  ) => {
    // if (accumulates[0] === ACCUMULATED_VALUE) return [];
    const columns: any[] = [];
    const { accumulatesConfig } = config || {};
    accumulates.forEach((value: string) => {
      const defaultWidth = value === 'region' || value === 'country' ? 260 : value === 'product' ? 290 : 200;
      const accumulateItemConfig = accumulatesConfig?.[value];
      // 兼容两种配置：数字（仅宽度）或对象（{ width, fixed }）
      const configWidth = typeof accumulateItemConfig === 'object'
        ? (accumulateItemConfig?.width || defaultWidth)
        : (accumulateItemConfig || defaultWidth);
      const configFixed = typeof accumulateItemConfig === 'object'
        ? accumulateItemConfig?.fixed
        : undefined;
      if (value !== 'date') {
        return columns.push({
          key: value,
          copyKey: value,
          header: formatMessage({ id: `common_${value}` }),
          align: 'left',
          width: configWidth,
          isDimColumn: true,
          ...(configFixed ? { fixed: configFixed } : {}),
          explain: value === 'product' ? getExplainVal(explainInfo, 'Product Name', getLocale()) : '',
          render(record: { country: string; dimension_name: { [x: string]: any; }; custom_dimensions?: Array<{ key: string; value: string; name_en?: string; name_ch?: string; }>; [key: string]: any; }) {
            if (isShowCountryFlag && value === 'country') {
              let ImgDom: JSX.Element;
              let ElementImgResDom: JSX.Element = (
                <span>{record.dimension_name?.[`${value}_name_${isEn ? 'en' : 'cn'}`] || '-'}</span>
              );
              try {
                ImgDom = (
                  <img
                    style={{ width: '24px', height: '16px', marginRight: '8px', verticalAlign: 'middle' }}
                    src={getCountryFlag(record.country === 'Total' ? 'Global' : record.country)}
                    alt='flag'
                  />
                );
              } catch (error) {
                ImgDom = (
                  <img
                    style={{ width: '24px', height: '16px', marginRight: '8px', verticalAlign: 'middle' }}
                    src={getCountryFlag('undefined')}
                    alt='flag'
                  />
                );
              }
              if (isRevenueCustomAccumulate) {
                ElementImgResDom = <span>{getDynamicLocalCnEnText(value, record[value], gameCode, getLocale(), selector)}</span>;
              }
              return (
                <>
                  {record[value] ? (
                    <div className='obd-table-flex'>
                      {value === 'country' && ImgDom}
                      {ElementImgResDom}
                    </div>
                  ) : (
                    '-'
                  )}
                </>
              );
            }
            // 在收入页面，接口返回的dimension_name 为null
            if (isRevenueCustomAccumulate) {
              return getDynamicLocalCnEnText(value, record[value], gameCode, getLocale(), selector);
            }

            // 优先从 dimension_name 获取标准维度
            const standardValue = record.dimension_name?.[`${value}_name_${isEn ? 'en' : 'cn'}`];
            if (standardValue) {
              return standardValue;
            }

            // 如果标准维度没有值，尝试从 custom_dimensions 获取自定义维度
            if (record.custom_dimensions && Array.isArray(record.custom_dimensions)) {
              const customDim = record.custom_dimensions.find((dim) => dim?.key === value);
              if (customDim && (customDim.name_en || customDim.name_ch || customDim.value)) {
                // 优先取中英文名称，其次取 value
                return (isEn ? (customDim.name_en || customDim.value) : (customDim.name_ch || customDim.value)) || '-';
              }
            }

            return '-';
          }
        });
      }
    });

    return columns;
  };

  // 指标列
  // 通常情况下metricsKey就是metricsInfo对象的key, 但是国家对比页面的metricsKey是受控的
  const getMetricColumns = (
    metricsKey: string[],
    metricsInfo: { [x: string]: any; },
    explainInfo: IExplain,
    selectType: string,
    metricsData?: any
  ) => {
    const isShowUpDown = selectType !== 'value_only';
    return metricsKey.reduce((columns: columsType[], key: string) => {
      const info = metricsInfo[key];
      const { metricsConfig } = info || {};
      const {
        type,
        rainbowKey,
        enPercentWidth,
        cnPercentWidth,
        enWidth,
        cnWidth,
        enName,
        cnName,
        explainEn = '',
        explainCn = '',
        tooltipEn,
        tooltipCn
      } = metricsConfig || {};
      const width = (isEn ? enPercentWidth : cnPercentWidth) + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT;
      const onlyValueWidth = (isEn ? enWidth : cnWidth) + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT;
      // 兼容模板字符串格式，如 "{{metricsInfo.total_users.en}}"
      const name = resolveAndSelectName(isEn, enName, cnName, metricsData?.metricsInfo) || '';
      const isPercent = type === 'percent';
      // 兼容老的解释, 后续配置更新完可删除
      const explainOld = getExplainVal(explainInfo, rainbowKey, getLocale(), tooltipEn || explainEn, tooltipCn || explainCn);
      const explain = resolveAndSelectName(isEn, tooltipEn || explainEn, tooltipCn || explainCn, metricsData?.metricsInfo, undefined, explainOld) || '';
      // @ts-ignore
      columns.push({
        key: key + selectType,
        copyKey: key,
        selectType,
        header: name,
        align: 'right',
        explain,
        width: isShowUpDown ? width : onlyValueWidth,
        isPercent,
        render(record) {
          const metric = record?.metrics?.[key] || {};
          const { progressWidth, metricValue } = metric;
          return (
            <>
              <div className={'table-text-bg'} style={{ width: `${progressWidth}%` }} />
              {/* <div className={'table-text-bg'} style={{width: `${progressWidth}%`}} /> */}
              <MetricAndUpDown
                metric={metricValue}
                updown={isShowUpDown ? { data: metric[`${selectType}PercentStr`], status: metric[`${selectType}Status`] } : null}
                hasTooltip={false}
              />
            </>
          );
        }
      });
      return columns;
    }, []);
  };

  // 通用 selector 维度列（按 key 渲染）
  const getSelectorDimensionColumn = (dimensionKey: string, header?: string, width = 120) => {
    return {
      key: dimensionKey,
      copyKey: dimensionKey,
      header: header || dimensionKey,
      width,
      fixed: 'left' as const,
      isDimColumn: true,
      align: 'left' as const,
      render(record: any) {
        const standardValue = record?.dimension_name?.[`${dimensionKey}_name_${isEn ? 'en' : 'cn'}`];
        if (standardValue) return standardValue;

        if (record?.custom_dimensions && Array.isArray(record.custom_dimensions)) {
          const customDim = record.custom_dimensions.find((dim: any) => dim?.key === dimensionKey);
          if (customDim && (customDim.name_en || customDim.name_ch || customDim.value)) {
            return (isEn ? (customDim.name_en || customDim.value) : (customDim.name_ch || customDim.value)) || '-';
          }
        }

        return record?.[dimensionKey] || '-';
      }
    };
  };

  // 自定义列
  const getCustomColumns = (customColumns: ICustomMetrics[] = [], explainInfo: IExplain, paging: PagingQuery) => {
    const columns: any[] = [];
    customColumns?.forEach((item) => {
      const { columnKey, valueKey, rainbowKey, nameEn, nameCn, enWidth, cnWidth, formatRule,
        fillZero, emptyValue, render, headerRender, sorter, tooltipEn, tooltipCn, fixed, align = 'left' } = item;
      const explain = getExplainVal(explainInfo, rainbowKey, getLocale(), tooltipEn, tooltipCn);
      const isDimColumn = Boolean(
        (item as any)?.isDimColumn ?? (item as any)?.type === 'dimension',
      );
      return columns.push({
        key: columnKey,
        copyKey: columnKey,
        header: headerRender ? headerRender?.(item) : isEn ? nameEn : nameCn,
        align,
        width: isEn ? enWidth : cnWidth,
        explain,
        sorter,
        fixed,
        // 给通过 customColumnsAfterDim / customDimension 传进来的“自定义列”打一个“这是维度列”的标记，让后续逻辑能把这些列当作“维度列”来处理，而不仅仅是普通指标列。
        ...(isDimColumn ? { isDimColumn: true } : {}),
        render(record: any, _: string, index: number) {
          if (typeof render === 'function') {
            return render(record, valueKey, paging, index);
          }

          // 支持从 custom_dimensions 数组中获取值
          if (record?.custom_dimensions && Array.isArray(record.custom_dimensions)) {
            const dimItem = record.custom_dimensions.find((dim: any) => dim?.key === columnKey);
            if (dimItem && (dimItem.name_en || dimItem.name_ch || dimItem.value)) {
              // 优先取中英文名称，其次取 value
              const dimValue = isEn ? (dimItem.name_en || dimItem.value) : (dimItem.name_ch || dimItem.value);
              if (formatRule && dimValue !== undefined && dimValue !== null) {
                return dataFormat({ value: dimValue, formatRule, fillZero, emptyValue }) || '-';
              }
              return dimValue || '-';
            }
          }

          // 原有的 valueKey 取值方式
          let value = get(record, valueKey);
          if (formatRule) {
            value = dataFormat({ value, formatRule, fillZero, emptyValue });
          }
          return value || "-";
        }
      });
    });
    return columns;
  };

  return {
    DATE_COLUMN,
    getAccumulateColumns,
    getMetricColumns,
    getCustomColumns,
    getSelectorDimensionColumn
  };
}

/**
 * 复制当前列，语言切换，对应当前语言的取值
 * @param data 当前行数据
 */
const getCopyRenderValues = (data: any, extendConfig?: { gameCode?: any; selector?: any; }) => {
  const isEn = getLocale() === 'en-US';
  if (!extendConfig) {
    const { dimension_name: dimensionName, custom_dimensions: customDimensions } = data || {};
    if (dimensionName) {
      Object.keys(data).forEach(key => {
        const value = data[key];
        data[key] = dimensionName?.[`${key}_${isEn ? 'en' : 'cn'}`] || value;
      });
    }
    // 处理 custom_dimensions: 将其展开到顶层以便复制
    if (customDimensions && Array.isArray(customDimensions)) {
      customDimensions.forEach((dim: { key: string; value: string; name_en?: string; name_ch?: string; }) => {
        if (dim?.key && !data[dim.key]) {
          // 优先取中英文名称，其次取 value
          data[dim.key] = (isEn ? (dim.name_en || dim.value) : (dim.name_ch || dim.value)) || '';
        }
      });
    }
  } else {
    data = generateCopyData(data, { gameCode: extendConfig?.gameCode, selectorList: extendConfig.selector });
  }
  return data;
};

// 表格返回数据格式整理
export function useTableDataFormat(
  tableData: any[],
  metricsInfo: metricsInfoType,
  timeType = "Day",
  isCopyData?: boolean,
  extendConfig?: { gameCode?: any; selector?: any; },
  metricsData?: any
) {
  const { formatMessage } = useIntl();
  return useMemo(() => {
    if (!Array.isArray(tableData)) return [];
    const WEEK_DAY_ARR = [
      formatMessage({ id: 'common_mon' }),
      formatMessage({ id: 'common_tues' }),
      formatMessage({ id: 'common_wed' }),
      formatMessage({ id: 'common_thur' }),
      formatMessage({ id: 'common_fri' }),
      formatMessage({ id: 'common_sat' }),
      formatMessage({ id: 'common_sun' }),
    ];
    return tableData.map((data) => {
      if (isCopyData) {
        data = getCopyRenderValues(data, extendConfig);
      }
      const { date, metrics, type, custom_dimensions: customDimensions, ...accumulates } = data || {};

      // 日期列数据 (如果type有值， 说明是聚合数据)
      // const dateStr = timeType === "Month" ? moment(date).format("YYYY-MM") : moment(date).format("YYYY-MM-DD") + getWeekDay(date, WEEK_DAY_ARR);
      let dateStr;

      if (date?.includes('~')) {
        dateStr = getDateFormatRangeString(timeType, date);
      } else {
        dateStr = getDateFormatString(timeType, date);
      }

      dateStr = (timeType === "Month" || dateStr.includes('~')) ? dateStr : dateStr + getWeekDay(dateStr, WEEK_DAY_ARR);

      // 指标列数据
      Object.keys(metricsInfo).forEach(key => {
        // if (!metricsInfo[key]) return; // 后端会返回全量指标的数据, 而前端页面可能只展示部分指标
        const { value, dod, sdlw, change } = metrics[key] || { value: "", dod: "", sdlw: "", change: "" };
        const { metricsConfig } = metricsInfo[key] || {};
        const { type, decimal, percentDecimals, percentDecimalsFloating = false, hideProgressBar = false, formatter, formatRule, dodFormatRule, sdlwFormatRule } = metricsConfig || {};

        // 兼容处理：如果没有 type 但有 formatRule，从 formatRule 推断 type
        let actualType = type;
        if (!actualType && formatRule) {
          const resolvedFormatRule = dynamicString(formatRule, metricsData) || '';
          // 从 formatRule 推断是否为百分比类型
          if (resolvedFormatRule.toLowerCase().includes('percent')) {
            actualType = 'percent';
          }
        }

        const isPercent = actualType === 'percent';
        const progressWidth = isPercent && !hideProgressBar ? value * 100 : 0; // 单元格内进度条的宽度

        // 优化：优先使用 formatRule，然后是 formatter，最后使用旧的 type + decimal 逻辑
        let metricValue;
        if (formatRule) {
          // 使用 formatRule 格式化（需要先通过 dynamicString 处理模板字符串）
          const resolvedFormatRule = dynamicString(formatRule, metricsData) || '';
          metricValue = resolvedFormatRule ? dataCommonFormat({ value, formatRule: resolvedFormatRule }) : commonValueFormatter(actualType, value, decimal);
        } else if (formatter) {
          metricValue = dataFormat({ value, formatRule: formatter });
        } else {
          metricValue = (key === 'accumulated_refund_rate' || key === 'lifetime_refund_rate') && value < 0 && isPercent ? `-${formatPercentNum(value, decimal, false)}` : commonValueFormatter(actualType, value, decimal);
        }

        const dodStatus = getUpAndDownStatus(dod, false);
        const sdlwStatus = getUpAndDownStatus(sdlw, false);
        const changeStatus = getUpAndDownStatus(change, false);

        // 优化：分开配置同比和环比的小数位，兼容旧逻辑
        let dodPercentStr;
        if (dodFormatRule) {
          const resolvedDodFormatRule = dynamicString(dodFormatRule, metricsData) || '';
          dodPercentStr = resolvedDodFormatRule ? dataCommonFormat({ value: dod, formatRule: resolvedDodFormatRule }) : formatPercentNum(dod, percentDecimals, percentDecimalsFloating);
        } else {
          dodPercentStr = formatPercentNum(dod, percentDecimals, percentDecimalsFloating);
        }

        let sdlwPercentStr;
        if (sdlwFormatRule) {
          const resolvedSdlwFormatRule = dynamicString(sdlwFormatRule, metricsData) || '';
          sdlwPercentStr = resolvedSdlwFormatRule ? dataCommonFormat({ value: sdlw, formatRule: resolvedSdlwFormatRule }) : formatPercentNum(sdlw, percentDecimals, percentDecimalsFloating);
        } else {
          sdlwPercentStr = formatPercentNum(sdlw, percentDecimals, percentDecimalsFloating);
        }

        const changePercentStr = formatPercentNum(change, percentDecimals, false);

        metrics[key] = {
          progressWidth: progressWidth > 100 ? 100 : progressWidth,
          metricValue,
          dodStatus,
          sdlwStatus,
          changeStatus,
          dodPercentStr,
          sdlwPercentStr,
          changePercentStr,
          ...metrics[key]
        };
      });

      return {
        date: dateStr,
        // 周六或者周日 表格行高亮
        isSatOrSun: timeType === "Day" && (moment(date).format('E') === '6' || moment(date).format('E') === '7'),
        metrics,
        custom_dimensions: customDimensions, // 将 custom_dimensions 传递到行数据中
        ...accumulates
      };
    });
    // if (customFormat) return customFormat(newData); // 为了配合外部的自定义格式整理
    // return newData;
  }, [tableData, getLocale(), metricsData]);
}
// 表格返回统计值数据格式整理 (和tableData用同一个hook时, 会因为useMemo调用次数太多而崩溃)
export function useStatisticsDataFormat(statisticsData: any[], metricsInfo: metricsInfoType, showStatistic: any[], isCopyData: boolean, metricsData?: any) {
  const { formatMessage } = useIntl();

  const statisticsObj = useMemo(() => {
    if (!Array.isArray(statisticsData)) return {};
    return statisticsData.reduce((acc, data) => {
      const { date, metrics, type: statisticsType, ...accumulates } = data || {};
      // 日期列数据 (如果type有值， 说明是聚合数据)
      const dateStr = formatMessage({ id: `common_${statisticsType}` }); // 统计值行的数据, 日期列要展示对应的聚合标题
      // 指标列数据
      Object.keys(metricsInfo).forEach(key => {
        const { value } = metrics[key] || { value: "", dod: "" };
        const { metricsConfig } = metricsInfo[key] || {};
        const { type, decimal, hideProgressBar, formatter, formatRule } = metricsConfig || {};

        // 兼容处理：如果没有 type 但有 formatRule，从 formatRule 推断 type
        let actualType = type;
        if (!actualType && formatRule) {
          const resolvedFormatRule = dynamicString(formatRule, metricsData) || '';
          // 从 formatRule 推断是否为百分比类型
          if (resolvedFormatRule.toLowerCase().includes('percent')) {
            actualType = 'percent';
          }
        }

        const isPercent = actualType === 'percent';
        // const { isPercent, decimals, isUserType } = metricsInfo[key] // TODO 如果指标信息的配置对象格式不同, 导致逻辑无法复用
        const progressWidth = isPercent && statisticsType !== 'count' && !hideProgressBar ? value * 100 : 0; // 单元格内进度条的宽度

        // 优化：优先使用 formatRule，然后是 formatter，最后使用旧的 type + decimal 逻辑
        let metricValue;
        if (isCopyData) {
          metricValue = value;
        } else if (statisticsType === 'count') {
          metricValue = formatNum(value, false, true);
        } else if (formatRule) {
          // 使用 formatRule 格式化（需要先通过 dynamicString 处理模板字符串）
          const resolvedFormatRule = dynamicString(formatRule, metricsData) || '';
          metricValue = resolvedFormatRule ? dataCommonFormat({ value, formatRule: resolvedFormatRule }) : commonValueFormatter(actualType, value, decimal);
        } else if (formatter) {
          metricValue = dataFormat({ value, formatRule: formatter });
        } else {
          metricValue = commonValueFormatter(actualType, value, decimal);
        }

        metrics[key] = {
          progressWidth: progressWidth > 100 ? 100 : progressWidth,
          metricValue,
          dodStatus: "no",
          sdlwStatus: "no",
          changeStatus: "no",
          dodPercentStr: "",
          sdlwPercentStr: "",
          changePercentStr: "",
          ...metrics[key]
        };
      });

      acc[statisticsType] = {
        date: dateStr,
        // 周六或者周日 表格行高亮
        isSatOrSun: false,
        metrics,
        ...accumulates
      };
      return acc;
    }, {});
  }, [statisticsData, getLocale(), metricsData]);
  return Object.keys(statisticsObj).length > 0 ? showStatistic.map(type => statisticsObj[type]) : [];
}

/**
 * 处理在 Day, Week , Month不同维度下, 显示的日期格式
 * @param dateDimension 日期维度
 * @param date 日期
 * @return 返回处理后符合产品要求的日期格式字符串
 */
export const getDateFormatString = (dateDimension: string, date: string) => {
  switch (dateDimension) {
    case "Week": {
      const weekOfDay = moment(date).format('E');
      return moment(date).subtract(Number(weekOfDay) - 1, 'days').format("YYYY-MM-DD");
    }
    case "Month": {
      return moment(date).format("YYYY-MM");
    }
    case "Day":
    default: {
      return moment(date).format("YYYY-MM-DD");
    }
  }
};

export const getDateFormatRangeString = (dateDimension: string, date: string) => {
  const [startDateStr, endDateStr] = date.split('~');

  const formattedStartDate = getDateFormatString(dateDimension === "Week" ? "Day" : dateDimension, startDateStr);
  const formattedEndDate = getDateFormatString(dateDimension === "Week" ? "Day" : dateDimension, endDateStr);

  if (formattedStartDate === formattedEndDate) {
    return formattedStartDate;
  }

  return `${formattedStartDate}~${formattedEndDate}`;
};

export const useMetricsColumns = (configData?: any) => {
  const getColumn = (configData: any) => {
    const columnObj: any = {};
    configData.forEach((item: any, index: any) => {
      const { metricKey } = item || {};
      columnObj[metricKey] = {
        index,
        metricsConfig: item,
      };
    });
    return columnObj;
  };
  // 如果后端获取不到会拿前端的配置，所以这里不用写默认columns
  return getColumn(configData);
};


